import { Injectable } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from '../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from '../core/logger.service';
import { SiteContextService, SiteSummary } from '../core/site-context.service';
import { from, Observable } from 'rxjs';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { AccountCustomFont, AccountVariable } from './account-settings-models';
import { Account } from '../back-office/account.model';

@Injectable()
export class AccountSettingsService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly siteContext: SiteContextService,
    private readonly requester: TriggerbeeRequester,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('AccountSettingsService');
  }

  public getAllAccountVariables(): Observable<Array<AccountVariable>> {
    return this.http.get(this.getGatewayResource('account-variables'));
  }

  public deleteAccountVariable(accountVariableId: string): Observable<AccountCustomFont> {
    return this.http.delete(this.getGatewayResource(`account-variables/${accountVariableId}`));
  }

  public updateAccountVariable(accountVariable: AccountVariable): Observable<AccountVariable> {
    accountVariable.siteId = this.siteContext.getCurrentSiteId();
    return this.http.put(this.getGatewayResource(`account-variables/${accountVariable.id}`), accountVariable);
  }

  public createAccountVariable(accountVariable: AccountVariable): Observable<AccountVariable> {
    accountVariable.siteId = this.siteContext.getCurrentSiteId();
    return this.http.post(this.getGatewayResource(`account-variables`), accountVariable);
  }

  public getAllCustomFonts(): Observable<Array<AccountCustomFont>> {
    return this.http.get(this.getGatewayResource('custom-fonts'));
  }

  public deleteCustomFont(customFontId: string): Observable<AccountCustomFont> {
    return this.http.delete(this.getGatewayResource(`custom-fonts/${customFontId}`));
  }

  public createCustomFont(customFont: AccountCustomFont): Observable<AccountCustomFont> {
    customFont.siteId = this.siteContext.getCurrentSiteId();
    return this.http.post(this.getGatewayResource('custom-fonts'), customFont);
  }

  public updateCustomFont(customFont: AccountCustomFont): Observable<AccountCustomFont> {
    return this.http.put(this.getGatewayResource(`custom-fonts/${customFont.id}`), customFont);
  }

  public sendResetUserPassword(email: string): Observable<any> {
    return from(
      this.requester
        .postAsync<boolean>(TriggerbeeApi.Gateway, `users/${email}/send-reset-password-email`)
        .then((response) => {
          if (!response.isPositive()) {
            this.logger.error('Reset user password request failed');
            return false;
          }
          return true;
        }),
    );
  }

  public getAllSites(): Observable<SiteSummary[]> {
    return from(
      this.siteContext.getAllSitesAsync().then((response) => {
        return response;
      }),
    );
  }

  public updateClientScriptGW(account: Account): Promise<void> {
    const request = [{ value: account.clientScript, path: '/ClientScript', op: 'replace' }];
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.patch(`${baseUrl}/accounts/${account.id}`, request).toPromise();
  }

  public createSiteStyles(
    colors: AccountVariable[],
    fonts: AccountCustomFont[],
    defaultFont: string,
  ): Observable<void> {
    const request = { colors, fonts, defaultFont };
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.post(`${baseUrl}/accounts/site-styles`, request);
  }

  public getFontLocalUrl(url: string): string {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return `${baseUrl}/site-scraper/site-styles/font?url=${encodeURI(url)}`;
  }

  private getGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/` + (subResource || '');
  }
}
